/* eslint max-lines: ["error", { "max": 985 }] */
import {
  ClassDefinitionItemDto,
  CreateEmployeeDto,
  EmployeeDto,
  EmployeeUpsertWarning,
  EmploymentType,
  Gender,
  PlanMarket,
  SubEntityDto,
  WageType,
} from '@zorro/clients';
import {
  getFieldRequirementsFromClassDefinitions,
  isDefined,
  logger,
  parsePhoneNumber,
  responseErrorToString,
} from '@zorro/common-utils';
import {
  formatDateISO,
  parseDateEnLocalePermissive,
  parseDateISO,
  parseStringToFloat,
} from '@zorro/shared/formatters';
import { ZorroError } from '@zorro/types';
import { callEndpoint } from '@zorro/web/ui-utils';
import { HttpStatusCode } from 'axios';

import {
  RosterInput,
  RosterUploadError,
  RosterUploadIssue,
  RosterUploadResult,
} from './uploadRoster.types';

export const INCORRECT_FILE_FORMAT_MESSAGE =
  'Incorrect roster format. Download the sample for the correct format.';

const friendlyFieldName = new Map<string, string>([
  ['firstName', 'First Name'],
  ['lastName', 'Last Name'],
  ['idFromEmployer', 'Employee HRIS ID'],
  ['subEntityName', 'Sub-entity'],
  ['subEntity2Name', 'Sub-entity 2'],
  ['email', 'Company Email'],
  ['personalEmail', 'Personal Email'],
  ['phone', 'Phone Number'],
  ['address', 'Address'],
  ['dateOfBirth', 'Date of Birth'],
  ['gender', 'Gender'],
  ['priorVendorPlanId', 'Current Plan ID'],
  ['priorVendorPlanMarket', 'Current Plan Market'],
  ['class', 'Class'],
  ['employmentType', 'Employment Type'],
  ['wageType', 'Wage Type'],
  ['customClassCategory', 'Custom Class Category'],
  ['salary', 'Yearly Salary'],
  ['hireDate', 'Hire Date'],
  ['eligibleFrom', 'ICHRA/CHOICE Eligibility Start Date'],
]);

function throwInvalidField(
  field: string,
  value: string,
  errorMessage = 'See sample roster file for details.'
) {
  throw new Error(
    `Invalid "${
      friendlyFieldName.get(field) || field
    }" : "${value}". ${errorMessage}`
  );
}

function throwRequiredField(
  field: string,
  errorMessage = 'Please include it in your input.'
) {
  throw new Error(
    `${
      friendlyFieldName.get(field) || field
    } is a required field. ${errorMessage}`
  );
}

function throwRequiredWhenFilled(field: string, filledField: string) {
  throw new Error(
    `${friendlyFieldName.get(field) || field} is required when ${
      friendlyFieldName.get(filledField) || filledField
    } is filled in.`
  );
}

function throwUnexpectedValue(
  field: string,
  value: string,
  errorMessage?: string
) {
  throw new Error(
    `${friendlyFieldName.get(field) || field}: Unexpected value "${value}". ${errorMessage}`
  );
}

/**
 * Since we can't iterate over fields of interfaces I've declared an object so I can use it's keys
 * with `Object.keys` and it's shape with `typeof EmployeeInput`, for more info:
 * https://stackoverflow.com/questions/45670705/iterate-over-interface-properties-in-typescript
 *
 * Note that these fields will always be strings since they are parsed from CSV input
 */
const getEmployeeInput = () => ({
  company_email: 'Company Email',
  personal_email: 'Personal Email',
  first_name: 'First Name',
  last_name: 'Last Name',
  id_from_employer: 'Id From Employer',
  sub_entity: 'Sub-entity 1',
  sub_entity_2: 'Sub-entity 2',
  phone: 'Phone',
  address: 'Address',
  date_of_birth: 'Date Of Birth',
  gender: 'Gender',
  plan_id: 'Plan Id',
  plan_market: 'Plan Market',
  employment_type: 'Employment Type',
  wage_type: 'Wage Type',
  salary: 'Salary',
  hire_date: 'Hire Date',
  eligibility_start_date: 'Eligibility Start Date',
  custom_class_category: 'Custom Class Category',
});

export type EmployeeInputType = ReturnType<typeof getEmployeeInput>;

interface FieldRequirements {
  hasWageType: boolean;
  hasEmploymentType: boolean;
  hasCustomClassCategoryInAllowance: boolean;
}

const processClassDefinitionResults = (
  classDefinitionResults: PromiseSettledResult<{
    items?: ClassDefinitionItemDto[];
  }>[]
): FieldRequirements => {
  const allClassDefinitions: ClassDefinitionItemDto[] = [];

  for (const result of classDefinitionResults) {
    if (result.status === 'fulfilled' && result.value.items) {
      allClassDefinitions.push(...result.value.items);
    }
  }

  const requirements =
    getFieldRequirementsFromClassDefinitions(allClassDefinitions);

  return {
    hasWageType: requirements.hasWageType,
    hasEmploymentType: requirements.hasEmploymentType,
    hasCustomClassCategoryInAllowance: requirements.hasCustomClassCategory,
  };
};

const getRequiredFieldsAndValidationInfo = async (
  employerId: string,

  years?: number[]
): Promise<{
  requiredFields: string[];
  hasCustomClassCategoryInAllowance: boolean;
}> => {
  const baseFields = [
    'firstName',
    'lastName',
    'dateOfBirth',
    'phone',
    'address',
    'email',
  ];

  if (years && years.length > 0) {
    try {
      const additionalFields: string[] = [];

      const classDefinitionResults = await Promise.allSettled(
        years.map((year) =>
          callEndpoint({
            method: 'allowanceModelControllerGetClassDefinitionByYear',
            params: [employerId, year],
          })
        )
      );

      const {
        hasWageType,
        hasEmploymentType,
        hasCustomClassCategoryInAllowance,
      } = processClassDefinitionResults(classDefinitionResults);

      if (hasWageType) {
        additionalFields.push('wageType');
      }
      if (hasEmploymentType) {
        additionalFields.push('employmentType');
      }
      if (hasCustomClassCategoryInAllowance) {
        additionalFields.push('customClassCategory');
      }

      return {
        requiredFields: [...baseFields, ...additionalFields],
        hasCustomClassCategoryInAllowance,
      };
    } catch (error) {
      logger.error(
        'Failed to fetch class definition for required fields:',
        error
      );
      return {
        requiredFields: baseFields,
        hasCustomClassCategoryInAllowance: false,
      };
    }
  }

  return {
    requiredFields: baseFields,
    hasCustomClassCategoryInAllowance: false,
  };
};

function parseByCsvFormatColumns(object: object): object is EmployeeInputType {
  const employeeInput = getEmployeeInput();
  return Object.keys(employeeInput).every((key: string) => {
    return key in object;
  });
}

function isValidEmail(email: string) {
  const expression = /^[^\s@]+@[^\s@][^\s.@]*\.[^\s@]+$/u;
  return expression.test(email);
}

function getInvalidSubEntityMessage(employerSubEntities: string[]) {
  return employerSubEntities.length > 0
    ? `Sub-entity must be one of ${employerSubEntities.join(',')}`
    : 'This organization does not support sub-entity';
}

function getInvalidSubEntity2Message(employerSubEntities2: string[]) {
  return employerSubEntities2.length > 0
    ? `Sub-entity 2 must be one of ${employerSubEntities2.join(',')}`
    : 'This organization does not support sub-entity 2';
}

function validateCustomAttributeWithInfo(
  employeeInput: EmployeeInputType,
  employeeClasses: string[],
  hasCustomClassCategoryInAllowance: boolean,
  isExistingEmployee: boolean
) {
  const customAttr = employeeInput.custom_class_category;
  const shouldHaveCustomAttr = hasCustomClassCategoryInAllowance;

  const isMissingOrEmpty = !isDefined(customAttr) || customAttr.trim() === '';

  if (shouldHaveCustomAttr) {
    const expectedClassesString = `Expected one of: "${employeeClasses.join('", "')}"`;
    if (isMissingOrEmpty) {
      // Existing employees keep their saved custom class category.
      if (!isExistingEmployee) {
        throwRequiredField('customClassCategory', expectedClassesString);
      }
    } else {
      const trimmedCustomAttr = customAttr.trim();
      if (!employeeClasses.includes(trimmedCustomAttr)) {
        throwInvalidField(
          'customClassCategory',
          trimmedCustomAttr,
          expectedClassesString
        );
      }
    }
    return;
  }
  if (!isMissingOrEmpty) {
    throwUnexpectedValue(
      'customClassCategory',
      customAttr,
      `This field should be left empty as custom class categories are not applicable based on your company's allowance setup`
    );
  }
}

async function validateRequiredFieldsWithInfo(
  createEmployeeDto: CreateEmployeeDto,
  requiredFields: string[]
) {
  for (const field of requiredFields) {
    if (field in createEmployeeDto) {
      const typedField = field as keyof CreateEmployeeDto;
      if (
        !createEmployeeDto[typedField] ||
        createEmployeeDto[typedField]?.toString().length === 0
      ) {
        throwRequiredField(
          field,
          `The violating employee is ${JSON.stringify(createEmployeeDto)}`
        );
      }
    }
  }
}

const normalizeStringToEnum = (value: string) => {
  return value.toUpperCase().replace(/[ -]/gu, '_');
};

const parseEmploymentType = (value: string): EmploymentType | null => {
  const normalized = normalizeStringToEnum(value);
  if (normalized === 'FULL') {
    return EmploymentType.FULL_TIME;
  }
  if (normalized === 'PART') {
    return EmploymentType.PART_TIME;
  }

  const employmentTypeValues = Object.values(EmploymentType) as string[];
  if (!employmentTypeValues.includes(normalized)) {
    return null;
  }

  return normalized as EmploymentType;
};

const parseWageType = (value: string): WageType | null => {
  if (!value) {
    return null;
  }

  const normalized = value.toUpperCase();

  if (normalized.includes('HOUR')) {
    return WageType.HOURLY;
  }

  if (normalized.includes('SALAR')) {
    return WageType.SALARY;
  }

  const wageTypeValues = Object.values(WageType) as string[];
  if (wageTypeValues.includes(normalized)) {
    return normalized as WageType;
  }

  return null;
};

const planMarketByNormalizedValue = new Map<string, PlanMarket>([
  ['ON', PlanMarket.ON_EXCHANGE],
  ['ON_EXCHANGE', PlanMarket.ON_EXCHANGE],
  ['OFF', PlanMarket.OFF_EXCHANGE],
  ['OFF_EXCHANGE', PlanMarket.OFF_EXCHANGE],
]);

// csvtojson omits a short CSV row's trailing cells, so a cell can be missing.
const trimCell = (cell?: string): string =>
  isDefined(cell) ? cell.trim() : '';

export const toPriorVendorPlanId = (cell?: string): string | null =>
  trimCell(cell) || null;

export const parsePlanMarket = (value?: string): PlanMarket | null =>
  planMarketByNormalizedValue.get(normalizeStringToEnum(trimCell(value))) ??
  null;

// eslint-disable-next-line sonarjs/cognitive-complexity
function validateFieldsFormat(createEmployeeDto: CreateEmployeeDto) {
  // validate fields format
  const personalEmail = createEmployeeDto.personalEmail;
  if (
    personalEmail &&
    personalEmail.length > 0 &&
    !isValidEmail(personalEmail)
  ) {
    throwInvalidField(
      'personal_email',
      `Can't parse email address: ${personalEmail}`
    );
  }

  const companyEmail = createEmployeeDto.email;
  if (!companyEmail) {
    throwRequiredField('company_email');
  }
  if (!isValidEmail(companyEmail)) {
    throwInvalidField('company_email', companyEmail);
  }

  if (
    createEmployeeDto.phone &&
    parsePhoneNumber(createEmployeeDto.phone) === undefined
  ) {
    throwInvalidField('phone', createEmployeeDto.phone);
  }

  if (createEmployeeDto.salary && Number.isNaN(createEmployeeDto.salary)) {
    throwInvalidField('salary', createEmployeeDto.salary.toString());
  }

  if (
    createEmployeeDto.dateOfBirth &&
    !parseDateISO(createEmployeeDto.dateOfBirth).isValid()
  ) {
    throwInvalidField('date_of_birth', createEmployeeDto.dateOfBirth);
  }

  if (
    createEmployeeDto.hireDate &&
    !parseDateISO(createEmployeeDto.hireDate).isValid()
  ) {
    throwInvalidField('hire_date', createEmployeeDto.hireDate);
  }

  if (
    createEmployeeDto.eligibleFrom &&
    !parseDateISO(createEmployeeDto.eligibleFrom).isValid()
  ) {
    throwInvalidField('eligibility_start_date', createEmployeeDto.eligibleFrom);
  }

  if (createEmployeeDto.gender) {
    const normalizedGender = normalizeStringToEnum(createEmployeeDto.gender);

    const genderValues = Object.values(Gender) as string[];
    if (!genderValues.includes(normalizedGender)) {
      throwInvalidField(
        'gender',
        createEmployeeDto.gender,
        `It must be one of : ${genderValues.join(',')}`
      );
    }
  }

  if (createEmployeeDto.idFromEmployer?.includes(' ')) {
    throwInvalidField(
      'id_from_employer',
      createEmployeeDto.idFromEmployer,
      'Should not contain spaces.'
    );
  }
}

export function validatePriorVendorPlanColumns(
  employeeInput: EmployeeInputType
): void {
  const planMarket = trimCell(employeeInput.plan_market);
  if (!planMarket) {
    return;
  }
  if (!parsePlanMarket(planMarket)) {
    throwInvalidField(
      'priorVendorPlanMarket',
      planMarket,
      'Current Plan Market must be "On-exchange" or "Off-exchange"'
    );
  }
  if (!toPriorVendorPlanId(employeeInput.plan_id)) {
    throwRequiredWhenFilled('priorVendorPlanId', 'priorVendorPlanMarket');
  }
}

function validateInputFieldsWithKnownInput(
  employeeInput: EmployeeInputType,
  employerSubEntities: SubEntityDto[] = [],
  employerSubEntities2: SubEntityDto[] = [],
  subEntity?: SubEntityDto,
  subEntity2?: SubEntityDto
) {
  if (
    employeeInput.employment_type &&
    !parseEmploymentType(employeeInput.employment_type)
  ) {
    throwInvalidField(
      'employment_type',
      employeeInput.employment_type,
      'Employment type must be "Full time" or "Part time"'
    );
  }
  if (employeeInput?.wage_type && !parseWageType(employeeInput.wage_type)) {
    throwInvalidField(
      'wage_type',
      employeeInput.wage_type,
      'Wage type must be "Salaried" or "Hourly"'
    );
  }

  if (employeeInput?.sub_entity && !subEntity) {
    throwInvalidField(
      'sub_entity',
      employeeInput.sub_entity,
      getInvalidSubEntityMessage(
        employerSubEntities.map((subEntity) => subEntity.name)
      )
    );
  }

  if (employeeInput?.sub_entity_2 && !subEntity2) {
    throwInvalidField(
      'sub_entity_2',
      employeeInput.sub_entity_2,
      getInvalidSubEntity2Message(
        employerSubEntities2.map((subEntity) => subEntity.name)
      )
    );
  }

  validatePriorVendorPlanColumns(employeeInput);
}

const fieldInputValidations = (
  employerSubEntities: SubEntityDto[],
  employerSubEntities2: SubEntityDto[],
  employeeInput: EmployeeInputType,
  employeeClasses: string[],
  hasCustomClassCategoryInAllowance: boolean,
  isExistingEmployee: boolean,
  subEntity?: SubEntityDto,
  subEntity2?: SubEntityDto
) => {
  validateInputFieldsWithKnownInput(
    employeeInput,
    employerSubEntities,
    employerSubEntities2,
    subEntity,
    subEntity2
  );

  validateCustomAttributeWithInfo(
    employeeInput,
    employeeClasses,
    hasCustomClassCategoryInAllowance,
    isExistingEmployee
  );
};

async function validateMergedEmployeeWithInfo(
  employeeInput: CreateEmployeeDto,
  requiredFields: string[]
) {
  await validateRequiredFieldsWithInfo(employeeInput, requiredFields);
  validateFieldsFormat(employeeInput);
}

async function processEmployeeRosterEntry(
  employerId: string,
  employeeInput: EmployeeInputType,
  employeeClasses: string[],
  sendActivationEmail: boolean,
  requiredFields: string[],
  hasCustomClassCategoryInAllowance: boolean,

  employerSubEntities: SubEntityDto[] = [],
  employerSubEntities2: SubEntityDto[] = [],
  existingEmployee?: EmployeeDto
): Promise<CreateEmployeeDto> {
  const subEntity = employerSubEntities.find(
    (subEntity) =>
      subEntity.name.toLocaleLowerCase() ===
      employeeInput.sub_entity.toLocaleLowerCase()
  );

  const subEntity2 = employerSubEntities2.find(
    (subEntity) =>
      subEntity.name.toLocaleLowerCase() ===
      employeeInput.sub_entity_2.toLocaleLowerCase()
  );

  fieldInputValidations(
    employerSubEntities,
    employerSubEntities2,
    employeeInput,
    employeeClasses,
    hasCustomClassCategoryInAllowance,
    isDefined(existingEmployee),
    subEntity,
    subEntity2
  );

  const employeeDataForProcessing = existingEmployee
    ? mergeWithExistingEmployee(
        employerId,
        employeeInput,
        existingEmployee,
        subEntity,
        subEntity2
      )
    : createNewEmployee(
        employerId,
        employeeInput,
        sendActivationEmail,
        subEntity?.id,
        subEntity2?.id
      );

  await validateMergedEmployeeWithInfo(
    employeeDataForProcessing,
    requiredFields
  );

  return employeeDataForProcessing;
}

export function mergeWithExistingEmployee(
  employerId: string,
  employeeInput: EmployeeInputType,
  existingEmployee: EmployeeDto,
  subEntity?: SubEntityDto,
  subEntity2?: SubEntityDto
): CreateEmployeeDto {
  return {
    employerId,
    subEntityId: subEntity?.id || existingEmployee.subEntityId,
    subEntity2Id: subEntity2?.id || existingEmployee.subEntity2Id,
    firstName: employeeInput.first_name || existingEmployee.firstName,
    lastName: employeeInput.last_name || existingEmployee.lastName,
    idFromEmployer:
      employeeInput.id_from_employer || existingEmployee.idFromEmployer,
    email: existingEmployee.email,
    personalEmail:
      employeeInput.personal_email || existingEmployee.personalEmail,
    address: employeeInput.address || existingEmployee.address,
    phone: employeeInput.phone || existingEmployee.phone,
    dateOfBirth: employeeInput.date_of_birth
      ? formatDateISO(parseDateEnLocalePermissive(employeeInput.date_of_birth))
      : existingEmployee.dateOfBirth,
    gender: employeeInput.gender
      ? (normalizeStringToEnum(employeeInput.gender) as Gender)
      : existingEmployee.gender,
    priorVendorPlanId: toPriorVendorPlanId(employeeInput.plan_id),
    priorVendorPlanMarket: parsePlanMarket(employeeInput.plan_market),
    employmentType: employeeInput.employment_type
      ? parseEmploymentType(employeeInput.employment_type)
      : existingEmployee.employmentType,
    wageType: employeeInput.wage_type
      ? parseWageType(employeeInput.wage_type)
      : existingEmployee.wageType,
    salary: employeeInput.salary
      ? parseStringToFloat(employeeInput.salary)
      : existingEmployee.salary,
    hireDate: employeeInput.hire_date
      ? formatDateISO(parseDateEnLocalePermissive(employeeInput.hire_date))
      : existingEmployee.hireDate,
    eligibleFrom: employeeInput.eligibility_start_date
      ? formatDateISO(
          parseDateEnLocalePermissive(employeeInput.eligibility_start_date)
        )
      : existingEmployee.eligibleFrom,
    eligibleUntil: existingEmployee.eligibleUntil,
    customClassCategory:
      employeeInput.custom_class_category ||
      existingEmployee.customClassCategory,
  };
}

export function createNewEmployee(
  employerId: string,
  employeeInput: EmployeeInputType,
  sendActivationEmail: boolean,
  subEntityId?: string,
  subEntity2Id?: string
): CreateEmployeeDto {
  return {
    employerId,
    firstName: employeeInput.first_name,
    lastName: employeeInput.last_name,
    idFromEmployer: employeeInput.id_from_employer,
    subEntityId,
    subEntity2Id,
    email: employeeInput.company_email,
    personalEmail:
      employeeInput.personal_email?.length > 0
        ? employeeInput.personal_email
        : null,
    address: employeeInput.address,
    phone: employeeInput.phone,
    dateOfBirth: formatDateISO(
      parseDateEnLocalePermissive(employeeInput.date_of_birth)
    ),
    gender: employeeInput.gender
      ? (normalizeStringToEnum(employeeInput.gender) as Gender)
      : null,
    priorVendorPlanId: toPriorVendorPlanId(employeeInput.plan_id),
    priorVendorPlanMarket: parsePlanMarket(employeeInput.plan_market),
    employmentType: parseEmploymentType(employeeInput.employment_type),
    wageType: employeeInput.wage_type
      ? parseWageType(employeeInput.wage_type)
      : null,
    salary: employeeInput.salary
      ? parseStringToFloat(employeeInput.salary)
      : null,
    hireDate: employeeInput.hire_date
      ? formatDateISO(parseDateEnLocalePermissive(employeeInput.hire_date))
      : null,
    eligibleFrom: employeeInput.eligibility_start_date
      ? formatDateISO(
          parseDateEnLocalePermissive(employeeInput.eligibility_start_date)
        )
      : null,
    customClassCategory: employeeInput.custom_class_category || null,
    sendActivationEmail,
  };
}

function logAndAddErrorToResult(
  result: RosterUploadResult,
  index: number,
  error: Error,
  message: string,
  employee: {
    firstName: string;
    lastName: string;
    email: string;
  }
) {
  logger.error(error, message);
  result.success = false;
  result.message =
    'One or more employees could not be created. Check `errors` for details.';
  result.errors.push({
    id: `${index} ${message}`,
    index,
    message,
    employeeDetails: {
      firstName: employee.firstName,
      lastName: employee.lastName,
      email: employee.email,
    },
  });
}

export function toRosterUploadWarning({
  index,
  message,
  employee,
}: EmployeeUpsertWarning): RosterUploadError {
  return {
    id: `${index} ${message}`,
    index,
    message,
    employeeDetails: {
      firstName: employee.firstName,
      lastName: employee.lastName,
      email: employee.email,
    },
  };
}

export function toRosterUploadIssues({
  errors,
  warnings,
}: RosterUploadResult): RosterUploadIssue[] {
  return [
    ...errors.map((error): RosterUploadIssue => ({
      ...error,
      severity: 'error',
    })),
    ...warnings.map((warning): RosterUploadIssue => ({
      ...warning,
      severity: 'warning',
    })),
  ];
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export async function createEmployeesFromRosterUpload(
  employerId: string,
  inputRoster: RosterInput[],
  sendActivationEmail: boolean,
  employeeClasses: string[],
  years?: number[]
): Promise<RosterUploadResult> {
  const rosterLength = inputRoster.length;

  const tryParseToCheckHeadersRow = parseByCsvFormatColumns(inputRoster[0]);
  if (!tryParseToCheckHeadersRow) {
    return {
      success: false,
      message: INCORRECT_FILE_FORMAT_MESSAGE,
      errors: [],
      warnings: [],
    };
  }

  if (rosterLength > 1000) {
    return {
      success: false,
      message:
        'Roster upload is limited to 1,000 employees. Please split the file.',
      errors: [],
      warnings: [],
    };
  }

  const result: RosterUploadResult = {
    success: false,
    message: '',
    errors: [],
    warnings: [],
  };
  const employeesToCreate: CreateEmployeeDto[] = [];

  let existingEmployeesMap: Map<string, EmployeeDto>;
  try {
    const existingEmployees = await callEndpoint({
      method: 'employeesControllerFindAll',
      params: [employerId],
    });

    existingEmployeesMap = new Map<string, EmployeeDto>(
      existingEmployees.map((employeeDto) => [
        employeeDto.email.toLocaleLowerCase(),
        employeeDto,
      ])
    );

    const employerSubEntities = await callEndpoint({
      method: 'subEntityControllerFindMany',
      params: [employerId],
    });

    const employerSubEntities2 = await callEndpoint({
      method: 'subEntity2ControllerFindMany',
      params: [employerId],
    });

    const { requiredFields, hasCustomClassCategoryInAllowance } =
      await getRequiredFieldsAndValidationInfo(employerId, years);

    logger.debug('Iterating roster to create employees...');
    for (let index = 0; index < rosterLength; index++) {
      const inputRow = inputRoster[index];
      const employeeRow: EmployeeInputType = inputRow as EmployeeInputType;

      try {
        const existingEmployee = existingEmployeesMap.get(
          employeeRow.company_email.toLocaleLowerCase()
        );
        const employee = await processEmployeeRosterEntry(
          employerId,
          employeeRow,
          employeeClasses,
          sendActivationEmail,
          requiredFields,
          hasCustomClassCategoryInAllowance,
          employerSubEntities,
          employerSubEntities2,
          existingEmployee
        );
        employeesToCreate.push(employee);
      } catch (error) {
        logger.error(error);
        const message = `${error.message}`;
        logAndAddErrorToResult(result, index, error, message, {
          firstName: employeeRow.first_name,
          lastName: employeeRow.last_name,
          email: employeeRow.company_email,
        });
      }
    }
  } catch (error) {
    return {
      success: false,
      message: `Failed to fetch existing employees. Try again later. ${responseErrorToString(error)}`,
      errors: [],
      warnings: [],
    };
  }

  if (employeesToCreate.length === 0) {
    return {
      ...result,
      message:
        'No entries were created due to input issues. Please review and try again',
    };
  }

  try {
    const createManyResult = await callEndpoint({
      method: 'employeesControllerUpsertMany',
      params: [{ employees: employeesToCreate }],
    });

    createManyResult.errors.forEach((error) => {
      logAndAddErrorToResult(
        result,
        error.index,
        new ZorroError(error.message),
        error.message,
        {
          firstName: error.employee.firstName,
          lastName: error.employee.lastName,
          email: error.employee.email,
        }
      );
    });
    // The monolith deploys separately and may not return warnings yet.
    result.warnings = (createManyResult.warnings ?? []).map(
      toRosterUploadWarning
    );

    const totalUpsertedCount = createManyResult.upsertedEmployees.length;
    const updatedCount = createManyResult.upsertedEmployees.filter((employee) =>
      existingEmployeesMap.has(employee.email.toLocaleLowerCase())
    ).length;

    const createdCount = totalUpsertedCount - updatedCount;

    if (result.errors.length === 0) {
      result.success = true;

      result.message = `${[
        createdCount > 0 ? `${createdCount} employees created` : null,
        updatedCount > 0 ? `${updatedCount} employees updated` : null,
      ]
        .filter(Boolean)
        .join(' and ')} successfully`;

      logger.info(result.message);
    } else {
      if (totalUpsertedCount > 0) {
        result.success = true;
        result.message = `${createdCount} employees created, ${updatedCount} updated, and ${result.errors.length} encountered errors. Review the errors to proceed`;
      } else {
        result.success = false;
        result.message =
          'No entries were created due to input issues. Please review and try again';
      }
      logger.error(result.message);
    }

    return result;
  } catch (error) {
    result.success = false;

    // The monolith only returns the per-employee shape for ValidationPipe
    // failures. Anything else (`AllowanceModelNotConfiguredError`, an
    // unmatched custom class category, ...) arrives as a flat list of
    // messages, so fall back to reading those instead of dropping them.
    const employeeFieldErrors: Record<string, Record<string, string>> | null =
      (error?.status === HttpStatusCode.BadRequest
        ? error?.body?.errors?.employees
        : null) ?? null;
    const errorCountBefore = result.errors.length;

    for (const key in employeeFieldErrors) {
      for (const field in employeeFieldErrors[key]) {
        logAndAddErrorToResult(
          result,
          Number.parseInt(key),
          new ZorroError(employeeFieldErrors[key][field]),
          employeeFieldErrors[key][field],
          {
            firstName:
              error?.employee?.firstName ??
              employeesToCreate[Number.parseInt(key)]?.firstName,
            lastName:
              error?.employee?.lastName ??
              employeesToCreate[Number.parseInt(key)]?.lastName,
            email:
              error?.employee?.email ??
              employeesToCreate[Number.parseInt(key)]?.email,
          }
        );
      }
    }

    if (result.errors.length === errorCountBefore) {
      logger.error(error, 'Roster upload failed');
      result.message = responseErrorToString(error);
    }

    return result;
  }
}
